import React, { useContext } from "react";
import { Field } from "react-final-form";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { IntlContext } from "context";
import { FileText, Clock3 } from "lucide-react";

const currencyFormatter = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });

type OfferPricingSectionProps = {
    amount: number;
    minSalary: number;
};

const OfferPricingSection: React.FC<OfferPricingSectionProps> = ({ amount, minSalary }) => {
    const intl = useContext(IntlContext);

    return (
        <>
            <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100/50 p-6">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500 text-white">
                        <FileText className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <h3 className="text-xs font-medium text-slate-700">
                        {intl.formatMessage("booking.messages.offer.amount.title")}
                    </h3>
                </div>
                <div className="mt-5 max-w-[320px]">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="offer-hourly-rate" className="text-xs font-normal text-slate-500">
                            {intl.formatMessage("booking.messages.offer.amount.hourly_rate")}
                        </Label>
                        <Field name="hourly_rate">
                            {({ input, meta }) => (
                                <>
                                    <div className="relative">
                                        <Input
                                            {...input}
                                            id="offer-hourly-rate"
                                            type="number"
                                            inputMode="decimal"
                                            autoComplete="off"
                                            min={minSalary}
                                            step="0.01"
                                            className="h-14 rounded-2xl border-slate-100 bg-white px-5 pr-10 text-base font-bold text-gray-900/70"
                                            required
                                            aria-invalid={meta.touched && Boolean(meta.error)}
                                            aria-describedby={meta.touched && meta.error ? "offer-hourly-rate-error" : undefined}
                                        />
                                        <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-base font-bold text-gray-900/50">
                                            €/h
                                        </span>
                                    </div>
                                    {meta.touched && meta.error ? (
                                        <p id="offer-hourly-rate-error" className="text-sm font-medium text-destructive" aria-live="polite">
                                            {meta.error}
                                        </p>
                                    ) : null}
                                </>
                            )}
                        </Field>
                    </div>
                </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100/50 p-6">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500 text-white">
                        <Clock3 className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <h3 className="text-xs font-medium text-slate-700">
                        {intl.formatMessage("booking.messages.offer.duration.title")}
                    </h3>
                </div>
                <div className="mt-5 flex flex-col gap-2">
                    <Label htmlFor="offer-duration" className="text-xs font-normal text-slate-500">
                        {intl.formatMessage("booking.messages.offer.duration.intervention_time")}
                    </Label>
                    <Field name="duration">
                        {({ input, meta }) => (
                            <>
                                <div className="relative max-w-[320px]">
                                    <Input
                                        {...input}
                                        id="offer-duration"
                                        type="number"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        min="1"
                                        step="1"
                                        className="h-14 rounded-2xl border-slate-100 bg-white px-5 pr-12 text-base font-bold text-gray-900/70"
                                        required
                                        aria-invalid={meta.touched && Boolean(meta.error)}
                                        aria-describedby={meta.touched && meta.error ? "offer-duration-error" : undefined}
                                    />
                                    <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-base font-bold text-gray-900/50">
                                        h
                                    </span>
                                </div>
                                {meta.touched && meta.error ? (
                                    <p id="offer-duration-error" className="text-sm font-medium text-destructive" aria-live="polite">
                                        {meta.error}
                                    </p>
                                ) : null}
                            </>
                        )}
                    </Field>
                    <div className="flex max-w-[320px] flex-col gap-2">
                        <p className="text-xs font-normal text-slate-500">
                            {intl.formatMessage("booking.messages.offer.amount.estimated_total")}
                        </p>
                        <div className="flex h-14 items-center rounded-2xl border border-slate-100 bg-white px-5">
                            <span className="text-base font-bold text-gray-900/70">
                                {currencyFormatter.format(Number.isFinite(amount) ? amount : 0)}
                            </span>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default OfferPricingSection;
